import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import api, { getDirectorPin, setDirectorPin } from "@/lib/api";
import { useAreaOpen } from "@/hooks/useAreaOpen";
import { useAuth } from "@/context/AuthContext";
import { canAccessArea } from "@/lib/areas";
import AreaDenied from "@/components/AreaDenied";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LockKey, Bank } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

export function useDirectorUnlocked() {
  const { user } = useAuth();
  const noPin = user && typeof user === "object" && user.org && user.org.pins_required && user.org.pins_required.director === false;
  const [unlocked, setUnlocked] = useState(!!getDirectorPin());
  useEffect(() => {
    const on = () => setUnlocked(!!getDirectorPin());
    window.addEventListener("director-locked", on);
    window.addEventListener("director-unlocked", on);
    return () => { window.removeEventListener("director-locked", on); window.removeEventListener("director-unlocked", on); };
  }, []);
  return unlocked || !!noPin;
}

export default function DirectorGate() {
  const { user } = useAuth();
  const { tr } = useLang();
  const unlocked = useDirectorUnlocked();
  const areaState = useAreaOpen("director");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;
    setBusy(true); setError("");
    try {
      await api.post("/director/unlock", { pin: pin.trim() });
      setDirectorPin(pin.trim());
      setPin("");
    } catch (err) {
      setError(err.response?.status === 403 ? tr("PIN errato. Riprova.", "Wrong PIN. Please try again.") : tr("Impossibile verificare il PIN.", "Unable to verify the PIN."));
    } finally { setBusy(false); }
  };

  if (areaState === "checking") return null;
  if (!canAccessArea(user, "director")) return <AreaDenied area="director" />;
  if (unlocked || areaState === "open") return <Outlet />;

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="director-gate">
      <form onSubmit={submit} className="w-full max-w-sm border border-border rounded-sm bg-card p-8 space-y-6 relative overflow-hidden">
        <Bank size={140} weight="fill" className="absolute -right-8 -bottom-8 text-primary/10" />
        <div className="relative">
          <div className="w-12 h-12 rounded-sm bg-primary/15 border border-primary/40 flex items-center justify-center text-primary mb-4"><LockKey size={26} weight="fill" /></div>
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-primary">{tr("Area riservata", "Restricted area")}</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl mt-1">{tr("Direttore Sportivo", "Sport Director")}</h1>
          <p className="text-sm text-muted-foreground mt-2">{tr("Inserisci il PIN per accedere a ingaggi, contratti, conteggi lista e budget di mercato.", "Enter the PIN to access wages, contracts, list counters and market budget.")}</p>
        </div>
        <div className="space-y-2 relative">
          <Input data-testid="director-pin-input" type="password" inputMode="numeric" autoFocus value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" className="text-center text-2xl tracking-[0.5em] font-display h-14" />
          {error && <p className="text-sm text-red-400" data-testid="director-pin-error">{error}</p>}
        </div>
        <Button type="submit" disabled={busy} data-testid="director-pin-submit" className="w-full font-display uppercase tracking-wide h-11">{busy ? tr("Verifica…", "Checking…") : tr("Sblocca", "Unlock")}</Button>
      </form>
    </div>
  );
}
